import { assign, uniq } from 'lodash-es';
import { isFunction, isNullish } from './is.js';
import chainFunctions from './chain.js';
function mergeObjects(target, source) {
    return assign({}, target, source);
}
/**
 * Converts a object-formatted style into a string to be used as a `style` attribute
 *
 * @param obj A style object
 * @returns A string, used as the value of the `style` attribute
 */
export function styleObject(obj) {
    let styleStr = '';
    for (const [k, v] of Object.entries(obj)) {
        if (isNullish(v)) {
            continue;
        }
        if (styleStr.length !== 0) {
            styleStr += ';';
        }
        styleStr += `${k}:${v}`;
    }
    return styleStr;
}
/**
 * Combine two style objects, outputting a single `style` attribute
 *
 * The `source` object overwrites properties, so if there are duplicate properties
 * between `source` and `target`, the ones in `source` will be reflected in the final attribute
 *
 * @param overrideable The
 * @param base
 */
export function mergeStyles(target, source) {
    if (isNullish(target)) {
        if (isNullish(source))
            return '';
        return styleObject(source);
    }
    if (isNullish(source))
        return styleObject(target);
    return styleObject(mergeObjects(target, source));
}
function mergeClasses(target, source) {
    return [target, source];
}
/**
 * Combines two strings. If the strings are the same value, it just uses 1 string
 * If 1 is nullish, it uses the other. Otherwise, it concatenates the two strings.
 *
 * This can theoretically be used to concatenate e.g. class strings. But we use it here to
 * concatenate aria attributes like `aria-describedby` that can take multiple `id` attribute references
 *
 * @param target
 * @param source
 */
function mergeStrings(target, source) {
    if (target === source)
        return target;
    return target + ' ' + source;
}
/**
 * Combine props from two sets of sources (e.g. a base or default source and user input),
 * potentially overriding props from the `target` input if they're present in `source`.
 *
 * The behavior of this function is somewhat complicated, but it works well for composing
 * generic components in most cases. It involves a few steps:
 *
 * 1. First, we combine the class attributes together using `clsx`-style array concatenation.
 * So if you had a target with class "foo bar" and a source with "baz", you'd get a class string
 * of "foo bar baz"
 * 2. We merge the style objects using the `mergeStyles` function
 * 3. We chain events, first running the `source` event followed by the `target` event.
 * 4. If there are any aria properties that take 1 or more `id` attributes as their value in both
 * `target` and `source`, we concatenate those values. (These are `aria-details`, `aria-describedby`,
 * `aria-labelledby`, `aria-owns`, `aria-controls`, `aria-errormessage`, and `aria-flowto`)
 * 5. For all other props, we overwrite the value in target with that in source if they are duplicated.
 *
 * @param target
 * @param source
 * @returns the actual attributes (that can be directly passed to svelte)
 */
export function mergeAttributes(target, source) {
    const final = {};
    // get a full, combined set of properties between target and source
    const uniqueProperties = uniq(Object.keys(target).concat(Object.keys(source)));
    for (const prop of uniqueProperties) {
        // @ts-expect-error this is basically impossible to type correctly
        final[prop] = getElementValue(prop, target, source);
    }
    return final;
}
export function getElementValue(key, target, source) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore this winds up being too complicated for typescript for some reason
    const targetValue = target[key];
    const sourceValue = source[key];
    if (key === 'style') {
        return mergeStyles(targetValue, sourceValue);
    }
    // if one of the 2 values is nullish, simply use the other
    if (isNullish(targetValue)) {
        return sourceValue;
    }
    if (isNullish(sourceValue)) {
        return targetValue;
    }
    if (key === 'class') {
        return mergeClasses(targetValue, sourceValue);
    }
    //  aria attributes that take 1 or more id attributes as value
    if ([
        'aria-details',
        'aria-describedby',
        'aria-labelledby',
        'aria-owns',
        'aria-controls',
        'aria-errormessage',
        'aria-flowto',
    ].includes(key)) {
        return mergeStrings(targetValue, sourceValue);
    }
    //  this is a bit hacky, but it effectively extracts whether the items are events
    if (isFunction(targetValue) && isFunction(sourceValue) && key[0] === 'o' && key[1] === 'n') {
        return chainFunctions(sourceValue, targetValue);
    }
    return sourceValue;
}
